import React, { useCallback, useMemo, useState } from 'react';
import { Empty, Pagination, Row, Typography } from 'antd';
import type { Dayjs } from 'dayjs';
import { useLazyGetDocumentsRegistryQuery } from '../../store/api/documentRegistryApi';
import type { DocumentRegistryRow } from '../../store/api/documentRegistryApi';
import {
  buildRegistrySearchParams,
  DocumentRegistryFilterDraft,
  getRegistryEmptyDescription,
  shouldFetchDocumentRegistry,
} from '../../utils/documentRegistryUi';
import DocumentRegistryFilters from './DocumentRegistryFilters';
import DocumentTable from './DocumentTable';
import DocumentPreviewModal from './DocumentPreviewModal';
import { useDocumentActions } from './useDocumentActions';

const { Text } = Typography;

const EMPTY_FILTERS: DocumentRegistryFilterDraft = {};

const DocumentRegistryTab: React.FC = () => {
  const [draftFilters, setDraftFilters] = useState<DocumentRegistryFilterDraft>(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<DocumentRegistryFilterDraft>(EMPTY_FILTERS);
  const [hasSearched, setHasSearched] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  const queryParams = useMemo(
    () =>
      buildRegistrySearchParams(
        {
          ...appliedFilters,
          dateFrom: appliedFilters.dateFrom,
          dateTo: appliedFilters.dateTo,
        },
        { page, limit, sortBy: 'createdAt', sortOrder: 'desc' }
      ),
    [appliedFilters, page, limit]
  );

  const [fetchRegistry, { data, isFetching, isError }] = useLazyGetDocumentsRegistryQuery();

  const refresh = useCallback(() => {
    if (hasSearched) {
      fetchRegistry(queryParams);
    }
  }, [fetchRegistry, hasSearched, queryParams]);

  const {
    previewVisible,
    previewImage,
    closePreview,
    handlePreview,
    handleDownload,
    handleDelete,
  } = useDocumentActions(refresh);

  const runSearch = useCallback(
    (nextPage = 1) => {
      setPage(nextPage);
      setHasSearched(true);
      const dateFrom = dateRange?.[0]?.format('YYYY-MM-DD');
      const dateTo = dateRange?.[1]?.format('YYYY-MM-DD');
      const nextApplied: DocumentRegistryFilterDraft = {
        ...draftFilters,
        ...(dateFrom ? { dateFrom } : {}),
        ...(dateTo ? { dateTo } : {}),
      };
      setAppliedFilters(nextApplied);
      fetchRegistry(
        buildRegistrySearchParams(nextApplied, { page: nextPage, limit, sortBy: 'createdAt', sortOrder: 'desc' })
      );
    },
    [dateRange, draftFilters, fetchRegistry, limit]
  );

  const handleReset = () => {
    setDraftFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setDateRange(null);
    setPage(1);
    setHasSearched(false);
  };

  const documents = shouldFetchDocumentRegistry(hasSearched) ? data?.documents ?? [] : [];
  const total = shouldFetchDocumentRegistry(hasSearched) ? data?.pagination?.total ?? 0 : 0;
  const emptyDescription = isError
    ? 'Ошибка загрузки реестра документов'
    : getRegistryEmptyDescription(hasSearched, total);

  return (
    <div>
      <DocumentRegistryFilters
        draftFilters={draftFilters}
        dateRange={dateRange}
        onDraftChange={setDraftFilters}
        onDateRangeChange={setDateRange}
        onSearch={() => runSearch(1)}
        onReset={handleReset}
      />

      {hasSearched && total > 0 ? (
        <Text type="secondary" style={{ display: 'block', marginBottom: 8 }}>
          Найдено: {total}
        </Text>
      ) : null}

      {!hasSearched || documents.length === 0 ? (
        <Empty description={emptyDescription} image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ padding: '48px 0' }} />
      ) : (
        <DocumentTable
          variant="registry"
          dataSource={documents as DocumentRegistryRow[]}
          loading={isFetching}
          onPreview={handlePreview}
          onDownload={handleDownload}
          onDelete={handleDelete}
        />
      )}

      {hasSearched && total > 0 ? (
        <Row justify="end" style={{ marginTop: 16 }}>
          <Pagination
            current={page}
            pageSize={limit}
            total={total}
            showSizeChanger
            pageSizeOptions={['25', '50', '100']}
            onChange={(p, ps) => {
              setPage(p);
              setLimit(ps);
              fetchRegistry(
                buildRegistrySearchParams(appliedFilters, { page: p, limit: ps, sortBy: 'createdAt', sortOrder: 'desc' })
              );
            }}
          />
        </Row>
      ) : null}

      <DocumentPreviewModal open={previewVisible} imageUrl={previewImage} onClose={closePreview} />
    </div>
  );
};

export default DocumentRegistryTab;
